import { useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { usePinTrigger } from "../hooks/usePinTrigger";
import OTPModal from "../modal/OTPModal";
import { clearTicketDetailFromStorage } from "../utils/ticket";

const PIN_EXCLUDE_SELECTORS = [
  "button",
  "input",
  "select",
  "textarea",
  "a",
  ".modal",
];

const isHomePath = (pathname: string) =>
  (pathname || "/").replace(/\/+$/, "") === "" || pathname === "/";

const GlobalPinHandler = () => {
  const [showPinModal, setShowPinModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;

  usePinTrigger({
    onTrigger: () => setShowPinModal(true),
    excludeSelectors: PIN_EXCLUDE_SELECTORS,
    clickDelay: 500,
    holdDuration: 5000,
  });

  const closeApp = useCallback(() => {
    const win = window as Window & {
      Android?: Record<string, (...args: unknown[]) => void>;
      android?: { app?: { exit?: () => void; close?: () => void } };
      kioskAPI?: { close?: () => void; systemExit?: () => void };
      electronAPI?: { close?: () => void; closeApp?: () => void; quit?: () => void };
      twaAPI?: { close?: () => void };
      chrome?: { app?: { close?: () => void } };
    };
    const nav = navigator as Navigator & {
      app?: { exitApp?: () => void; close?: () => void };
    };
    toast.success("PIN verified. Closing the app.");

    const attempts = [
      () => win.kioskAPI?.close?.(),
      () => win.kioskAPI?.systemExit?.(),
      () => win.electronAPI?.close?.(),
      () => win.electronAPI?.closeApp?.(),
      () => win.electronAPI?.quit?.(),
      () => win.Android?.finish?.(),
      () => win.Android?.finishAffinity?.(),
      () => win.Android?.exitApp?.(),
      () => win.Android?.closeApp?.(),
      () => win.Android?.moveTaskToBack?.(true),
      () => nav.app?.exitApp?.(),
      () => win.twaAPI?.close?.(),
      () => win.android?.app?.exit?.(),
      () => win.chrome?.app?.close?.(),
      () => window.close(),
    ];

    attempts.forEach((attempt, index) => {
      window.setTimeout(() => {
        try {
          attempt();
        } catch {
          /* try the next close method */
        }
      }, index * 80);
    });

    window.setTimeout(() => {
      if (isHomePath(window.location.pathname)) {
        window.location.replace("about:blank");
      }
    }, attempts.length * 80 + 200);
  }, []);

  const handlePinSuccess = useCallback(() => {
    setShowPinModal(false);
    if (isHomePath(pathRef.current)) {
      closeApp();
      return;
    }
    clearTicketDetailFromStorage();
    toast.success("PIN verified. Returning to the home screen.");
    navigate("/", { replace: true });
  }, [closeApp, navigate]);

  return (
    <OTPModal
      show={showPinModal}
      onHide={() => setShowPinModal(false)}
      onSuccess={handlePinSuccess}
    />
  );
};

export default GlobalPinHandler;
